// studioMachine — the Studio's view as one state machine (#256): one pure
// step, `studioStep(state, event, facts) -> { state, effects }`. `facts` are
// projected from the project (the cards' names, the selection) and never
// stored, so the machine cannot disagree with them; every id the state holds
// is resolved against them before each step and each render
// (`resolveStudioView`): one that left the desk is dropped. Effects are data;
// the shell's runner (StudioCentre.tsx) maps each onto an existing door.
//
// Slice 1 holds MF-1, renaming a name in place (#253): the Interaction sum is
// Idle | Renaming; a failed save leaves a note under the name; the last
// rename is kept for the module toolbar's Undo. Later slices add the front
// things (Find, Ask, menus) and gestures as Interaction states, and place.

export type { StudioEffect, StudioEvent, StudioFacts, StudioViewState };
export { initialStudioView, resolveStudioView, studioStep };

import { exhaustSwitchCase } from "@bilda/utils";
import { cleanName } from "../RowOverflow/cleanName.js";

// One gesture or front thing at a time.
type Interaction =
    | { readonly type: "idle" }
    | {
          readonly type: "renaming";
          readonly id: string;
          // made: a container just made, named in place; existing: MF-1.
          readonly origin: "made" | "existing";
          // Enter on a blank name: the field stays open with its note.
          readonly blank: boolean;
      };

// What shows under a card's name: a rename that did not save.
type StudioNote = { readonly type: "rename_failed"; readonly previous: string };

type StudioViewState = {
    readonly interaction: Interaction;
    readonly notes: ReadonlyMap<string, StudioNote>;
    // The last rename saved here, for its Undo.
    readonly undo: { readonly id: string; readonly previous: string } | null;
};

type StudioFacts = {
    // The name of every card on the desk.
    readonly titles: ReadonlyMap<string, string>;
    readonly selected: ReadonlySet<string>;
};

type StudioEvent =
    | { readonly type: "name_clicked"; readonly id: string }
    | { readonly type: "name_double_clicked"; readonly id: string }
    // "…" menu > Rename.
    | { readonly type: "rename_chosen"; readonly id: string }
    // A container was made (async: it arrives after its command answered).
    | { readonly type: "container_made"; readonly id: string }
    // Enter, or a click outside with a changed name.
    | { readonly type: "rename_submitted"; readonly text: string }
    // Esc, or a click outside with a blank or unchanged name.
    | { readonly type: "rename_cancelled" }
    | {
          readonly type: "rename_failed";
          readonly id: string;
          readonly previous: string;
      }
    | { readonly type: "undo_chosen"; readonly id: string };

type StudioEffect =
    | { readonly type: "select_only"; readonly id: string }
    // Saved at once (optimistic); `previous` is what a failure goes back to.
    | {
          readonly type: "rename";
          readonly id: string;
          readonly title: string;
          readonly previous: string;
      };

type StudioStep = {
    readonly state: StudioViewState;
    readonly effects: readonly StudioEffect[];
};

const IDLE: Interaction = { type: "idle" };

const initialStudioView: StudioViewState = {
    interaction: IDLE,
    notes: new Map(),
    undo: null,
};

// Every id checked against the desk: what left it is treated as absent.
const resolveStudioView = (
    state: StudioViewState,
    facts: StudioFacts,
): StudioViewState => {
    const here = (id: string) => facts.titles.has(id);
    const interaction =
        state.interaction.type === "renaming" && !here(state.interaction.id)
            ? IDLE
            : state.interaction;
    const notes = [...state.notes.keys()].every(here)
        ? state.notes
        : new Map([...state.notes].filter(([id]) => here(id)));
    const undo = state.undo === null || here(state.undo.id) ? state.undo : null;
    return interaction === state.interaction &&
        notes === state.notes &&
        undo === state.undo
        ? state
        : { interaction, notes, undo };
};

const without = <V>(
    map: ReadonlyMap<string, V>,
    id: string,
): ReadonlyMap<string, V> =>
    map.has(id) ? new Map([...map].filter(([k]) => k !== id)) : map;

const onlySelected = (facts: StudioFacts, id: string): boolean =>
    facts.selected.size === 1 && facts.selected.has(id);

const stay = (state: StudioViewState): StudioStep => ({ state, effects: [] });

// The field opens (its failure note gives way to it), and the card is the
// one selected.
const startRenaming = (
    state: StudioViewState,
    facts: StudioFacts,
    id: string,
    origin: "made" | "existing",
): StudioStep => ({
    state: {
        ...state,
        interaction: { type: "renaming", id, origin, blank: false },
        notes: without(state.notes, id),
    },
    effects:
        origin === "made" || onlySelected(facts, id)
            ? []
            : [{ type: "select_only", id }],
});

const studioStep = (
    current: StudioViewState,
    event: StudioEvent,
    facts: StudioFacts,
): StudioStep => {
    const state = resolveStudioView(current, facts);
    const { interaction } = state;
    switch (event.type) {
        case "name_clicked":
            if (interaction.type !== "idle") return stay(state);
            // A click on the name of the one selected card edits it; any
            // other click on a name selects that card first.
            return onlySelected(facts, event.id)
                ? startRenaming(state, facts, event.id, "existing")
                : { state, effects: [{ type: "select_only", id: event.id }] };
        case "name_double_clicked":
        case "rename_chosen":
            return interaction.type === "idle"
                ? startRenaming(state, facts, event.id, "existing")
                : stay(state);
        // A field never pops up late over something else (bug 3, #256).
        case "container_made":
            return interaction.type === "idle"
                ? startRenaming(state, facts, event.id, "made")
                : stay(state);
        case "rename_submitted": {
            if (interaction.type !== "renaming") return stay(state);
            const { id } = interaction;
            const previous = facts.titles.get(id) ?? "";
            const title = cleanName(event.text);
            if (title === "")
                return stay({
                    ...state,
                    interaction: { ...interaction, blank: true },
                });
            if (title === previous)
                return stay({ ...state, interaction: IDLE });
            return {
                state: {
                    interaction: IDLE,
                    notes: without(state.notes, id),
                    undo: { id, previous },
                },
                effects: [{ type: "rename", id, title, previous }],
            };
        }
        case "rename_cancelled":
            return interaction.type === "renaming"
                ? stay({ ...state, interaction: IDLE })
                : stay(state);
        // The name went back (the shell's door); one quiet line says so, and
        // there is nothing to undo.
        case "rename_failed":
            return stay({
                ...state,
                notes: new Map(state.notes).set(event.id, {
                    type: "rename_failed",
                    previous: event.previous,
                }),
                undo: state.undo?.id === event.id ? null : state.undo,
            });
        case "undo_chosen": {
            const { undo } = state;
            if (undo === null || undo.id !== event.id) return stay(state);
            return {
                state: { ...state, undo: null },
                effects: [
                    {
                        type: "rename",
                        id: undo.id,
                        title: undo.previous,
                        previous: facts.titles.get(undo.id) ?? "",
                    },
                ],
            };
        }
        default:
            return exhaustSwitchCase(event);
    }
};
